import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAllChangeRequests, updateChangeRequestStatus } from '@/data/changeRequests';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, XCircle, Clock, AlertCircle, Video } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function ChangeRequests() {
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'complete' | 'reject'>('complete');
  const [adminResponse, setAdminResponse] = useState('');

  const { data: requests, isLoading } = useQuery({
    queryKey: ['all-change-requests'],
    queryFn: getAllChangeRequests,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status, response }: { id: string; status: any; response?: string }) =>
      updateChangeRequestStatus(id, status, response),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-change-requests'] });
      toast.success('Change request updated');
      setDialogOpen(false);
      setAdminResponse('');
      setSelectedRequest(null);
    },
    onError: (error: any) => {
      toast.error('Failed to update: ' + error.message);
    },
  });

  const openDialog = (request: any, type: 'complete' | 'reject') => {
    setSelectedRequest(request);
    setDialogType(type);
    setDialogOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedRequest) return;
    const status = dialogType === 'complete' ? 'completed' : 'rejected';
    updateMutation.mutate({
      id: selectedRequest.id,
      status,
      response: adminResponse || undefined,
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      open: { variant: 'default', icon: Clock, label: 'Open' },
      in_progress: { variant: 'secondary', icon: Clock, label: 'In Progress' },
      completed: { variant: 'default', icon: CheckCircle2, label: 'Completed', className: 'bg-green-100 text-green-800' },
      rejected: { variant: 'destructive', icon: XCircle, label: 'Rejected' },
    };
    const config = variants[status] || variants.open;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Change Requests</h1>
          <p className="text-muted-foreground">
            Review and manage vendor change requests
          </p>
        </div>

        {isLoading ? (
          <div>Loading change requests...</div>
        ) : requests?.length === 0 ? (
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No change requests</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {requests?.map((request) => (
              <Card key={request.id} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold text-xl mb-1">
                      {request.products?.title || 'Unknown Product'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Vendor: {request.products?.vendor_name || 'Unknown'} ({request.products?.vendor_email})
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Submitted: {format(new Date(request.created_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  {getStatusBadge(request.status)}
                </div>

                <Separator className="my-4" />

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Change Notes</h4>
                    <p className="text-sm whitespace-pre-wrap bg-muted p-3 rounded">
                      {request.change_notes}
                    </p>
                    {request.video_url && (
                      <div className="mt-3">
                        <Button variant="outline" size="sm" asChild>
                          <a href={request.video_url} target="_blank" rel="noopener noreferrer">
                            <Video className="h-4 w-4 mr-2" />
                            Watch Video
                          </a>
                        </Button>
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm mb-2">AI Summary</h4>
                    <div className="bg-blue-50 p-3 rounded mb-3">
                      <p className="text-sm text-blue-900">
                        {request.ai_summary || 'No AI summary available'}
                      </p>
                    </div>
                    
                    {request.ai_required_assets && Array.isArray(request.ai_required_assets) && request.ai_required_assets.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Required Assets</h4>
                        <ul className="text-sm space-y-1">
                          {(request.ai_required_assets as string[]).map((asset: string, idx: number) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-primary mr-2">•</span>
                              {asset}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {request.admin_response && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Admin Response</h4>
                      <p className="text-sm bg-muted p-3 rounded">
                        {request.admin_response}
                      </p>
                    </div>
                  </>
                )}

                {request.status === 'open' && (
                  <>
                    <Separator className="my-4" />
                    <div className="flex gap-3">
                      <Button
                        onClick={() => openDialog(request, 'complete')}
                        disabled={updateMutation.isPending}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Mark Complete
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => openDialog(request, 'reject')}
                        disabled={updateMutation.isPending}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject Request
                      </Button>
                    </div>
                  </>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogType === 'complete' ? 'Mark as Complete' : 'Reject Request'}
            </DialogTitle>
            <DialogDescription>
              {dialogType === 'complete'
                ? 'Add a note about how the changes were implemented (optional)'
                : 'Provide feedback to the vendor about why this request was rejected'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="admin_response">
                {dialogType === 'complete' ? 'Completion Notes (Optional)' : 'Rejection Reason'}
              </Label>
              <Textarea
                id="admin_response"
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                rows={4}
                placeholder={
                  dialogType === 'complete'
                    ? 'Describe what changes were made...'
                    : 'Explain why this request cannot be completed...'
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={updateMutation.isPending}
              variant={dialogType === 'complete' ? 'default' : 'destructive'}
            >
              {updateMutation.isPending ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
